<template>
  <div id="gateway">
    <nav-tabs-extended>
      <template slot="meta-bar-left">
        <span v-if="gateway.name" :title="gateway.name" class="max-width mr-4">{{ gateway.name }}</span>
        <span>{{ eui }}</span>
        <span :title="gateway.description" class="ml-4 d-none d-sm-inline max-width">{{ gateway.description }}</span>
        <span v-if="gateway.connection === true" class="text-success ml-4">{{ $t('g-gateway:connected') | capitalize }}</span>
        <span v-else-if="gateway.connection === false" class="text-danger ml-4">{{ $t('g-gateway:disconnected') | capitalize }}</span>
        <span v-else-if="gateway.eui" class="ml-4">{{ $t('g-gateway:neverConnected') | capitalize }}</span>
      </template>

      <template slot="meta-bar-right">
        <span class="ml-lg-4 d-none d-lg-block">{{ gateway.fleet ? gateway.fleet.name : '' }}</span>
      </template>

      <template slot="tabs-bar">
        <div class="nav-item"><router-link id="gatewayOverviewLink" :to="{name: 'gatewayOverview', params: {eui}}" class="nav-link">{{ $t('g-gateway:overview') | capitalize }}</router-link></div>

        <b-dropdown v-if="canManageGateway && (
                      $store.getters.bscFeature(gateway, 'management') ||
                      $store.getters.bscFeature(gateway, 'configuration') ||
                      $store.getters.bscFeature(gateway, 'control') ||
                      $store.getters.bscFeature(gateway, 'version')
                    )"
                    variant="link"
                    no-caret
                    class="nav-item"
                    boundary="window"
        >
          <a id="gatewayConfigurationButton" slot="button-content" :class="{active: $route.meta.dropdown==='configurations'}" class="nav-link">{{ $t('g-gateway:configurations') | capitalize }}<i class="icon-angle-right ml-2" /></a>

          <b-dropdown-item v-if="isRole.superAdmin && $store.getters.bscFeature(gateway, 'management')" id="gatewayConfigurationSnmpLink" :to="{name: 'gatewayConfSnmp', params: {eui}}">{{ $t('g-gateway:confSnmp') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="$store.getters.bscFeature(gateway, 'configuration') && $store.getters.bscFeature(gateway, 'control')" id="gatewayConfigurationAlarmsLink" :to="{name: 'gatewayConfAlarms', params: {eui}}">{{ $t('g-gateway:confAlarms') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="$store.getters.bscFeature(gateway, 'configuration')" id="gatewayConfigurationClockLink" :to="{name: 'gatewayConfClock', params: {eui}}">{{ $t('g-gateway:confClock') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="isRole.superAdmin && $store.getters.bscFeature(gateway, 'configuration')" id="gatewayConfigurationVpnLink" :to="{name: 'gatewayConfVpn', params: {eui}}">{{ $t('g-gateway:confVpn') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="$store.getters.bscFeature(gateway, 'configuration')" id="gatewayConfigurationBackhaulLink" :to="{name: 'gatewayConfBackhaul', params: {eui}}">{{ $t('g-gateway:confBackhaul') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="$store.getters.bscFeature(gateway, 'configuration')" id="gatewayConfigurationRadioLink" :to="{name: 'gatewayConfRadio', params: {eui}}">{{ $t('g-gateway:confRadio') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="$store.getters.bscFeature(gateway, 'configuration') && $store.getters.gatewayFeature(gateway, 'gps') && $store.getters.gatewayFeature(gateway, 'aesKey')" id="gatewayConfigurationGeolocationLink" :to="{name: 'gatewayConfGeolocation', params: {eui}}">{{ $t('g-gateway:confGeolocation') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="$store.getters.bscFeature(gateway, 'version')" id="gatewayConfigurationSoftwareLink" :to="{name: 'gatewayConfSoftware', params: {eui}}">{{ $t('g-gateway:confSoftware') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="$store.getters.bscFeature(gateway, 'configuration')" id="gatewayConfigurationHistoryLink" :to="{name: 'gatewayConfHistory', params: {eui}}">{{ $t('g-gateway:confHistory') | capitalize }}</b-dropdown-item>
        </b-dropdown>

        <div class="nav-item"><router-link id="gatewayKpiLink" :to="{name: 'gatewayKpi', params: {eui}}" class="nav-link">{{ $t('g-gateway:kpi') | capitalize }}</router-link></div>

        <b-dropdown variant="link" no-caret class="nav-item" boundary="window">
          <a id="gatewayDataButton" slot="button-content" :class="{active: $route.meta.dropdown==='data'}" class="nav-link">{{ $t('g-gateway:data') | capitalize }}<i class="icon-angle-right ml-2" /></a>

          <b-dropdown-item id="gatewayDataUpLink" :to="{name: 'gatewayDataUp', params: {eui}}">{{ $t('g-gateway:dataUp') | capitalize }}</b-dropdown-item>
          <b-dropdown-item id="gatewayDataDownLink" :to="{name: 'gatewayDataDown', params: {eui}}">{{ $t('g-gateway:dataDown') | capitalize }}</b-dropdown-item>
        </b-dropdown>

        <b-dropdown v-if="canManageGateway && ($store.getters.bscFeature(gateway, 'specificCommand') || $store.getters.bscFeature(gateway, 'remoteSsh'))" variant="link" no-caret class="nav-item" boundary="window">
          <a id="gatewayRemoteButton" slot="button-content" :class="{active: $route.meta.dropdown==='remote'}" class="nav-link">{{ $t('g-gateway:remote') | capitalize }}<i class="icon-angle-right ml-2" /></a>

          <b-dropdown-item v-if="$store.getters.bscFeature(gateway, 'specificCommand')" id="gatewayRemoteFileExplorerLink" :to="{name: 'gatewayFileExplorer', params: {eui}}">{{ $t('g-gateway:fileExplorer') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="$store.getters.bscFeature(gateway, 'remoteSsh')" id="gatewayRemoteSshLink" :to="{name: 'gatewayShell', params: {eui}}">{{ $t('g-gateway:shell') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="$store.getters.bscFeature(gateway, 'specificCommand')" id="gatewayRemoteCommandLink" :to="{name: 'gatewayCommand', params: {eui}}">{{ $t('g-gateway:command') | capitalize }}</b-dropdown-item>
        </b-dropdown>

        <div v-if="isRole.user && $store.getters.bscFeature(gateway, 'spectrumAnalysis') && $store.getters.gatewayFeature(gateway, 'spectrumAnalysis')" class="nav-item"><router-link id="gatewaySpectrumsLink" :to="{name: 'gatewaySpectrumsList', params: {eui}}" class="nav-link">{{ $t('g-gateway:spectrum') | capitalize }}</router-link></div>

        <b-dropdown v-if="canManageGateway" variant="link" no-caret class="nav-item" boundary="window">
          <a id="gatewayDiagnosticButton" slot="button-content" :class="{active: $route.meta.dropdown==='diagnostic'}" class="nav-link">{{ $t('g-gateway:diagnostic') | capitalize }}<i class="icon-angle-right ml-2" /></a>

          <b-dropdown-item id="gatewayDiagnosticTasksLink" :to="{name: 'gatewayTasks', params: {eui}}">{{ $t('g-gateway:tasks') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="$store.getters.bscFeature(gateway, 'snmpLog')" id="gatewayDiagnosticSnmpLink" :to="{name: 'gatewaySnmp', params: {eui}}">{{ $t('g-gateway:snmp') | capitalize }}</b-dropdown-item>
          <b-dropdown-item id="gatewayDiagnosticEventsLink" :to="{name: 'gatewayEvents', params: {eui}}">{{ $t('g-gateway:events') | capitalize }}</b-dropdown-item>
          <b-dropdown-item id="gatewayDiagnosticNetworkEventsLink" :to="{name: 'gatewayNetworkEvents', params: {eui}}">{{ $t('g-gateway:networkEvents') | capitalize }}</b-dropdown-item>
        </b-dropdown>

        <!-- used to pull the menu to the right -->
        <div style="flex: 1;" />

        <b-dropdown id="gateway-menu" variant="link" no-caret right boundary="window">
          <i slot="button-content" class="icon-more" />

          <b-dropdown-item v-if="this.$store.getters.isGatewayInSelection(gateway)" id="gatewayRemoveFromSelectionButton" @click="removeFromSelection">{{ $t('g-gateway:removeFromSelection') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-else id="gatewayAddToSelectionButton" @click="addToSelection">{{ $t('g-gateway:addToSelection') | capitalize }}</b-dropdown-item>
          <b-dropdown-item id="gatewayMenuCopy">{{ $t('g-gateway:copy') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="canManageGateway && $store.getters.bscFeature(gateway, 'specificCommand')" id="gateway-menu-reboot" @click="confirmRebootGateway">{{ $t('g-gateway:reboot') | capitalize }}</b-dropdown-item>
          <b-dropdown-item v-if="isRole.admin" id="gateway-menu-delete" @click="confirmDeleteGateway">{{ $t('g-gateway:delete') | capitalize }}</b-dropdown-item>
        </b-dropdown>
      </template>
    </nav-tabs-extended>

    <router-view />

    <confirm id="rebootGateway"
             v-model="showRebootGatewayConfirm"
             :message="$t('g-gateway:confirmReboot', { gateway: gateway.eui })"
             @confirm:validate="rebootGateway"
    />

    <confirm id="deleteGateway"
             v-model="showDeleteGatewayConfirm"
             :message="$t('g-gateway:confirmDelete', { gateway: gateway.eui })"
             @confirm:validate="deleteGateway"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'
import constants from '@/configuration/constants'
import ClipboardJS from 'clipboard'

import BDropdown from 'bootstrap-vue/es/components/dropdown/dropdown'
import BDropdownItem from 'bootstrap-vue/es/components/dropdown/dropdown-item'

export default {
  components: { BDropdown, BDropdownItem },

  props: {
    eui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      clipboard: null,
      refreshTimeout: null,
      showRebootGatewayConfirm: false,
      showDeleteGatewayConfirm: false
    }
  },

  computed: {
    ...mapGetters(['gateway']),

    // If user's customerId = gateway customerId, it's one of my gateways
    // We use the link 'customer' of the gateway fleet to get the customerId of the gateway
    isMyGateway () {
      const customerLink = this.$utils.getLinkHref(this.gateway.fleet, 'customer') || ''
      const customerId = Number(customerLink.split('/').pop())
      return this.$store.getters.authenticatedUserCustomer.id === customerId
    },

    canManageGateway () {
      return this.isRole.superAdmin || (this.isRole.admin && this.isMyGateway)
    }
  },

  watch: {
    eui () {
      this.getGateway()
    },

    gateway () {
      // set refresh timeout of the gateway
      clearTimeout(this.refreshTimeout)

      // In case the promise ended after the destruction of the component
      // Check the component status to only relaunch the polling when component is not destroyed
      if (this._isDestroyed || this._isBeingDestroyed) return
      this.refreshTimeout = setTimeout(() => { this.getGateway() }, constants.gatewayOverviewRefresh)
    }
  },

  created () {
    if (this.eui !== this.gateway.eui) {
      this.$store.commit('setGateway', {})
    }

    this.getGateway()
    this.clipboard = new ClipboardJS('#gatewayMenuCopy', { text: this.getGatewayInformation })
  },

  destroyed () {
    clearTimeout(this.refreshTimeout)

    this.clipboard.destroy()
    this.clipboard = null
  },

  methods: {
    getGateway () {
      this.$store.dispatch('getGateway', this.eui)
        .then(() => {
          this.$store.commit('addLastViewedGateway', this.gateway)
        })
        .catch((e) => {
          if (e.response && e.response.status === 404) eventBus.$emit('request:404')
          if (e.response && e.response.status === 403) eventBus.$emit('request:403')
        })
    },

    addToSelection () {
      if (this.gateway) {
        this.$store.commit('addGatewayInSelection', this.gateway)
      }
    },

    removeFromSelection () {
      if (this.gateway) {
        this.$store.commit('removeGatewayFromSelection', this.gateway)
      }
    },

    getGatewayInformation () {
      let gwInfo = `EUI: ${this.gateway.eui}\n`
      gwInfo += `Name: ${this.gateway.name}\n`
      gwInfo += `Fleet: ${this.gateway.fleet ? this.gateway.fleet.name : '-'}\n`
      gwInfo += `Type: ${this.gateway.type ? this.gateway.type.name : '-'}\n`
      gwInfo += `Description: ${this.gateway.description}\n`
      gwInfo += `Region: ${this.gateway.region}` + (this.gateway.country ? ` (${this.gateway.country})` : '') + `\n`
      gwInfo += `Visibility: ${this.gateway.visibility}\n`
      gwInfo += `IP: ${this.gateway.ip}:${this.gateway.port}\n`
      gwInfo += `Connected: ${this.gateway.connection}\n`
      gwInfo += `Door: ${this.gateway.door}\n`
      gwInfo += `GPS: ${this.gateway.gps}\n`
      gwInfo += `\n`
      gwInfo += `URL: ${location.href}`

      return gwInfo
    },

    confirmRebootGateway () {
      this.showRebootGatewayConfirm = true
    },

    async rebootGateway () {
      let error
      [error] = await to(this.$api.createGatewayCommand(this.gateway, 'reboot'))
      if (error) return

      // Display success message
      eventBus.$emit('message-success', this.$t('g-gateway:rebootSuccess', { gateway: this.gateway.eui }))
    },

    confirmDeleteGateway () {
      this.showDeleteGatewayConfirm = true
    },

    async deleteGateway () {
      let error
      [error] = await to(this.$api.deleteGateway(this.gateway))
      if (error) return

      const eui = this.gateway.eui

      // Remove from selection if it is in
      this.removeFromSelection()
      // Reset the store
      this.$store.commit('setGateway', {})
      // Redirect to the list
      this.$router.replace('/gateways')
      // Display success message
      eventBus.$emit('message-success', this.$t('g-gateway:deleteSuccess', { gateway: eui }))
    }
  }
}
</script>

<style scoped lang="scss">
  .max-width {
    max-width: 20vw;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
